/* eslint-disable no-undef */
import { createConsumer } from '@rails/actioncable';
import axios from 'axios';
import { keysToSnakeCase, keysToCamel } from './object';
import queryString from 'query-string';
import qs from 'qs';
import { showNotification } from './notify';

const codeErrorHandler = {};

// -------- TZ helpers --------
let CACHED_TZ = null;
const getLocalTimezone = () => {
  if (CACHED_TZ) return CACHED_TZ;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    CACHED_TZ = tz || 'UTC';
  } catch (_) {
    CACHED_TZ = 'UTC';
  }
  return CACHED_TZ;
};

// -------- REQUEST interceptor: agrega X-Timezone y Accept (si faltan) --------
axios.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  if (!config.headers['X-Timezone']) {
    config.headers['X-Timezone'] = getLocalTimezone(); // ej: "America/Caracas"
  }
  if (!config.headers.Accept) {
    config.headers.Accept = 'application/json';
  }
  return config;
});

// -------- RESPONSE interceptor --------
const retrieveFilename = (headers) => {
  let name = '';
  if (
    headers['content-disposition'] &&
    headers['content-disposition'].split("UTF-8''").length === 2
  ) {
    name = decodeURIComponent(
      headers['content-disposition'].split("UTF-8''")[1]
    );
  }
  return name;
};

axios.interceptors.response.use(
  (response) => {
    if (response.config.responseType === 'blob') {
      response.filename = retrieveFilename(response.headers);
      return response;
    }
    return {
      ...response,
      data: keysToCamel(response.data),
    };
  },
  (error) => {
    const requestUrl = error.config?.url || '';
    const isLogoutRequest = requestUrl.includes('/sign_out');

    if (!isLogoutRequest && error.response && error.response.data) {
      const data = error.response.data;
      const errorMessage = typeof data === 'string'
        ? data
        : (data.error && typeof data.error === 'object' ? data.error.message : data.error) || data.message;

      if (errorMessage) {
        console.log('Error message from API:', errorMessage);
        showNotification('error', errorMessage);
      }
    }

    if (
      error.response &&
      error.response.status &&
      typeof codeErrorHandler[error.response.status] === 'function'
    ) {
      codeErrorHandler[error.response.status]();
    }

    // Siempre rechazar con el error completo para preservar la estructura
    return Promise.reject(error);
  }
);

export const setCodeErrorHandler = (code, handler) => {
  codeErrorHandler[code] = handler;
};

export const API_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL + '/api'
  : '/api';
export const appendBase = (url) => `${API_URL}${url}`;

export const get = (url, params = {}, config = {}) => {
  url +=
    Object.keys(params).length > 0
      ? '?' + queryString.stringify(keysToSnakeCase(params))
      : '';
  return axios.get(appendBase(url), {
    maxRedirects: 0,
    headers: {
      Accept: 'application/json',
      // X-Timezone lo añade el interceptor si no viene
    },
    ...config,
  });
};

export const getWithArrayParams = (url, params = {}, config = {}) => {
  const snake = keysToSnakeCase(params);
  const query =
    Object.keys(snake).length > 0
      ? '?' +
      qs.stringify(snake, {
        arrayFormat: 'brackets',
        skipNulls: true,
      })
      : '';
  return axios.get(appendBase(url + query), {
    maxRedirects: 0,
    headers: { Accept: 'application/json' },
    ...config,
  });
};

export const post = (url, payload, options = {}, config = {}) => {
  if (options.transformKeys !== false) {
    payload = keysToSnakeCase(payload);
  }
  return axios.post(appendBase(url), payload, {
    maxRedirects: 0,
    headers: {
      Accept: 'application/json',
    },
    ...config,
  });
};

export const put = (url, payload, options = {}, config = {}) => {
  if (options.transformKeys !== false) {
    payload = keysToSnakeCase(payload);
  }
  return axios.put(appendBase(url), payload, {
    maxRedirects: 0,
    headers: {
      Accept: 'application/json',
    },
    ...config,
  });
};

export const patch = (url, payload, options = {}, config = {}) => {
  if (options.transformKeys !== false) {
    payload = keysToSnakeCase(payload);
  }
  return axios.patch(appendBase(url), payload, {
    maxRedirects: 0,
    headers: {
      Accept: 'application/json',
    },
    ...config,
  });
};

export const destroy = (url, payload, config = {}) => {
  return axios.delete(appendBase(url), {
    maxRedirects: 0,
    headers: {
      Accept: 'application/json',
    },
    data: keysToSnakeCase(payload || {}),
    ...config,
  });
};

export const setAuthToken = (token) => {
  // No pisar otros headers comunes
  axios.defaults.headers.common = axios.defaults.headers.common || {};
  axios.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const deleteAuthToken = () => {
  if (axios.defaults.headers.common) {
    delete axios.defaults.headers.common.Authorization;
  }
};

export const getWS = () => {
  let url = process.env.REACT_APP_WS_URL;

  if (!url) {
    const protocol = process.env.NODE_ENV === 'production' ? 'wss' : 'ws';
    const host = window.location.host;
    url = `${protocol}://${host}`;
  }

  const authHeader = axios.defaults.headers.common?.Authorization || '';
  const token = authHeader ? (authHeader.split(' ')[1] || '') : '';
  const tz = encodeURIComponent(getLocalTimezone());

  return createConsumer(`${url}/cable?token=${token}&tz=${tz}`);
};

const request = { get, post, put, patch, destroy, getWithArrayParams };

export default request;
